import * as React from 'react';
import { BusinessGroup } from 'components/search/utils';
import { Container } from 'ui';
import { assertNever } from '@tock/shared/utils/typeUtils';
import { consumer, core } from '@tock/coretypes';
import { makeStyles } from '@material-ui/core/styles';
import SearchLauncher, {
  CITY_SEARCH_GA_CATEGORY,
  GROUP_SEARCH_GA_CATEGORY,
  SearchLauncherType,
} from 'components/www/availability-search/SearchLauncher';
import clsx from 'clsx';

export const Y_OFFSET_SIZE_PX = 40;

const useStyles = makeStyles((theme) => ({
  root: {
    background: theme.colors.bgDefault,
    border: `1px solid ${theme.colors.borderDefault}`,
    marginBottom: 0,
    marginLeft: 'auto',
    marginRight: 'auto',
    position: 'relative',
    zIndex: 1,
    width: `calc(100% - ${theme.spacing(4)}px)`,
    borderRadius: 8,
    [theme.breakpoints.up('md')]: {
      borderRadius: Y_OFFSET_SIZE_PX,
      minHeight: theme.spacing(10),
      paddingLeft: 0,
      paddingRight: 0,
    },
    [theme.breakpoints.only('sm')]: {
      width: 500,
    },
    [theme.breakpoints.down('xs')]: {
      width: `calc(100% - ${theme.spacing(5)}px)`,
    },
    [theme.breakpoints.down('sm')]: {
      borderBottom: `2px solid ${theme.colors.borderDefault}`,
      boxSizing: 'border-box',
      padding: theme.spacing(2.5),
    },
  },
  card: {
    [theme.breakpoints.down('sm')]: {
      borderRadius: 0,
    },
  },
  maxWidth: {
    maxWidth: 1062,
  },
  fullWidth: {
    maxWidth: 1440,
  },
}));

export const getExperienceTypes = (): SearchLauncherType[] => {
  return ['reservations', 'pickup', 'delivery', 'events', 'wineries', 'all'];
};

interface SearchLauncherWidgetProps {
  metroArea?: consumer.IMetroArea;
  businessGroup?: BusinessGroup;
  defaultType?: SearchLauncherType;
  fullWidth?: boolean;
}

function getDefaultType(
  defaultCategory: core.DefaultSearchCategory | null | undefined
): SearchLauncherType {
  if (!defaultCategory) {
    return 'reservations';
  }
  switch (defaultCategory) {
    case 'PICKUP':
      return 'pickup';
    case 'DINE_IN':
      return 'reservations';
    default:
      assertNever(defaultCategory);
  }
}

const SearchLauncherWidget: React.FunctionComponent<SearchLauncherWidgetProps> = ({
  defaultType,
  businessGroup,
  metroArea,
  fullWidth = false,
}) => {
  const [activeType, setActiveType] = React.useState<SearchLauncherType>(
    defaultType ? defaultType : getDefaultType(metroArea?.defaultSearchCategory)
  );
  React.useEffect(() => {
    if (defaultType) {
      return;
    }
    setActiveType(getDefaultType(metroArea?.defaultSearchCategory));
  }, [defaultType, metroArea?.defaultSearchCategory]);

  const experienceTypes = getExperienceTypes();

  const onChange = (value: SearchLauncherType) => {
    setActiveType(value);
    if (!window.analytics) {
      return;
    }
    const type = experienceTypes.find((type) => type === value);
    if (!type) {
      return;
    }
    window.analytics.sendEvent(
      businessGroup ? GROUP_SEARCH_GA_CATEGORY : CITY_SEARCH_GA_CATEGORY,
      'Type select',
      type
    );
  };
  const classes = useStyles();

  return (
    <Container
      className={clsx(
        classes.root,
        {
          [classes.maxWidth]: !fullWidth,
          [classes.fullWidth]: fullWidth,
        },
        'search-launcher-container'
      )}
    >
      <SearchLauncher
        experienceTypes={experienceTypes}
        metroArea={metroArea}
        businessGroup={businessGroup}
        onChange={onChange}
        type={activeType}
      />
    </Container>
  );
};

export default SearchLauncherWidget;
